import { Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject } from 'rxjs';
import { filter, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslationKeys } from 'src/app/contracts';
import { AppSettings } from 'src/app/core/common/app.settings';
import { APP_DATE_FORMAT, APP_DATETIME_FORMAT, IAppSettings, Logs, NgEvent, settings$ } from 'src/app/core/contracts';
import { Logger, styledLog } from 'src/app/core/helpers/logger';
import { UI } from 'src/app/core/helpers/ui';
import { svgIcons } from 'src/app/icons/svg-icons';

@Component({
  template: '',
})
export abstract class BaseComponent implements OnInit, OnDestroy {
  protected host = inject(ElementRef<HTMLElement>);
  protected logger? = inject(Logger);
  protected ngEvents = new Subject<NgEvent>();
  public settings?: IAppSettings;
  public translation = TranslationKeys;
  public update$ = new BehaviorSubject<void>(undefined);
  protected ui = inject(UI);
  protected icons = svgIcons;

  constructor() {
    settings$.pipe(takeUntil(this.onDestroy)).subscribe(settings => (this.settings = settings));
  }
  protected onInit = this.ngEvents.pipe(filter(event => event === NgEvent.OnInit));
  protected onDestroy = this.ngEvents.pipe(filter(event => event === NgEvent.OnDestroy));
  ngOnInit(): void {
    if (Logs.ENABLE_LIFE_CYCLE_LOGS) {
      styledLog(
        {
          msg:
            (this.host?.nativeElement as HTMLElement)?.tagName?.toLowerCase() +
            ':ngOnInit' /* params: this, dom: this.host?.nativeElement heavy on the console */,
        },
        { color: '#5555FF' }
      );
    }
    this.ngEvents.next(NgEvent.OnInit);
  }
  ngOnDestroy(): void {
    if (Logs.ENABLE_LIFE_CYCLE_LOGS) {
      styledLog(
        {
          msg: this.componentName + ':ngOnDestroy' /* params: this, dom: this.host?.nativeElement heavy on the console */,
        },
        { color: '#AA5500' }
      );
    }
    this.ngEvents.next(NgEvent.OnDestroy);
    this.ngEvents.complete();
  }
  public isRtl() {
    return this.settings && AppSettings.isRtlLanguage(this.settings.language);
  }
  public isLtr() {
    return !this.isRtl();
  }
  public getDateFormat() {
    return this.settings?.dateFormat || APP_DATE_FORMAT;
  }
  public getDateTimeFormat() {
    return this.settings?.dateTimeFormat || APP_DATETIME_FORMAT;
  }
  protected get componentName() {
    return (this.host?.nativeElement as HTMLElement)?.tagName?.toLowerCase();
  }
  protected onComponentUpdate<T>(action: Observable<T>, sideEffect: (result: T) => void) {
    return this.update$.pipe(
      takeUntil(this.onDestroy),
      switchMap(() => action),
      tap(sideEffect)
    );
  }
  public get onLanguageChanged$() {
    return this.ui.onLanguageChanged$.pipe(startWith(null));
  }
  protected onLanguageChanged() {
    if (!this.ui) {
      if (this.logger) {
        this.logger.warn({
          element: (this.host?.nativeElement as HTMLElement)?.tagName?.toLowerCase(),
          event: '@onLanguageChanged()',
          component: this,
          message: 'Cannot register language change callback: UI service cannot be resolved',
        });
      }
      return EMPTY;
    }
    return this.onLanguageChanged$.pipe(takeUntil(this.onDestroy));
  }
  public pluralize(count: number, singular: string, plural: string): string {
    if (count === undefined || count === null) {
      return '';
    }
    if (count === 1 || count === 0) {
      return this.ui.translate.instant(singular) as string;
    }
    return this.ui.translate.instant(plural) as string;
  }
}
